/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectARS.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting an ARS profile with search functionality.
    Used in Class of Services for assigning outbound routing strategy.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by ARS name and description

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface ArsProfile {
  id: number;
  name: string;
  description?: string;
}

interface SelectARSProps {
  value: number | null;
  onChange: (value: number | null) => void;
  profiles: ArsProfile[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  id?: string;
}

export default function SelectARS({
  value,
  onChange,
  profiles,
  placeholder = 'Select ARS Profile',
  disabled = false,
  className = '',
  error = false,
  id
}: SelectARSProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected profile
  const selectedProfile = value ? profiles.find(p => p.id === value) : null;

  // Filter profiles based on search
  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (profile.description && profile.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (profileId: number | null) => {
    onChange(profileId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md text-sm
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedProfile ? '' : 'text-muted-foreground'}>
          {selectedProfile ? selectedProfile.name : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search ARS profiles..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {/* Clear selection option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`
                w-full px-3 py-2 text-left rounded-md text-sm
                hover:bg-accent hover:text-accent-foreground
                flex items-center justify-between
                transition-colors
                ${!value ? 'bg-accent text-accent-foreground' : ''}
              `}
            >
              <span className="text-muted-foreground italic">None</span>
              {!value && <Check className="h-4 w-4 text-primary" />}
            </button>

            {filteredProfiles.length > 0 ? (
              filteredProfiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => handleSelect(profile.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md text-sm
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                    ${value === profile.id ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{profile.name}</div>
                    {profile.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {profile.description}
                      </div>
                    )}
                  </div>
                  {value === profile.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No ARS profiles found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

